import React, { useRef, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import calenderImage from '../../assets/images/calender.svg';

interface SingleDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  disabled?: boolean;
}
/**
 * Reusable single date picker component
 * Matches the design of SelectDatePicker (date range picker) but for single date selection
 * Uses the same styling: date-filter-input-container class and calendar icon
 * Directly opens date picker popup on click (no nested menu)
 */
const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  value,
  onChange,
  label = 'Select Date',
  disabled = false,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleDateChange = (newValue: Date | null) => {
    if (newValue) {
      // Set time to start of day to ensure consistent date handling
      const dateAtStartOfDay = moment(newValue).startOf('day').toDate();
      onChange(dateAtStartOfDay);
    } else {
      onChange(null);
    }
    setOpen(false);
  };

  // Format date for display (same format as used in SelectDatePicker)
  const formattedDate = value ? moment(value).format('MM/DD/YYYY') : label;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div style={{ position: 'relative', display: 'inline-block' }} className="width-100">
        {/* Custom styled trigger that looks like date-filter-input-container */}
        <div 
          ref={triggerRef}
          onClick={() => !disabled && setOpen(true)}
          className="date-filter-input-container"
          style={{ 
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            gap: 8,
            cursor: disabled ? 'not-allowed' : 'pointer', 
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <p
            className="date-filter-input"
            style={{
              margin: 0,
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'clamp(12px, 2.8vw, 16px)',
            }}
            title={formattedDate}
          >
            {formattedDate}
          </p>
          <img
            src={calenderImage}
            alt="calendar"
            style={{ flexShrink: 0, width: 20, height: 20 }}
          />
        </div>
        
        {/* Hidden DatePicker that opens popup when trigger is clicked */}
        <div style={{ position: 'absolute', top: 0, left: 0, opacity: 0, pointerEvents: 'none', width: '100%', height: '100%' }}>
          <DatePicker
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={value}
            onChange={handleDateChange}
            disabled={disabled}
            format="MM/dd/yyyy"
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  '& .MuiInputBase-root': {
                    height: '44px',
                    width: '100%',
                  },
                },
              },
              popper: {
                anchorEl: triggerRef.current,
                placement: 'bottom-start',
              },
            }}
          />
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default SingleDatePicker;

